import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
  Popover,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Switch,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import api from "../../axiosInstance";
import { ToastContainer } from "react-toastify";
import MentorRescheduleDialog from "./MentorRescheduleDialog";
import MentorCancelBookingDialog from "./MentorCancelBookingDialog";

const ACCENT = "#17479e";

const whiteSortLabelSx = {
  color: "white !important",
  "&.Mui-active": { color: "white !important" },
  "&:hover": { color: "white !important" },
  "& .MuiTableSortLabel-icon": { color: "white !important" },
};

const cardStyle = {
  borderRadius: 3,
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.6)",
};

const iconBtnSx = {
  border: "1px solid rgba(0,0,0,0.08)",
  borderRadius: 2,
};

// Session types come from /mentor-catalog/sessionTypes (dynamic, id-based) —
// known names get a fixed color, anything else falls back to the palette
// below (keyed by sessionTypeId so the color stays stable per type).
const SESSION_TYPE_COLORS = {
  "1:1 Session": { color: "#5e35b1", bg: "#ede7f6" },
  "Group Session": { color: "#1565c0", bg: "#e3f2fd" },
  "Doubt Session": { color: "#e65100", bg: "#fff3e0" },
};
const SESSION_TYPE_PALETTE = [
  { color: "#6a1b9a", bg: "#f3e5f5" },
  { color: "#00695c", bg: "#e0f2f1" },
  { color: "#ad1457", bg: "#fce4ec" },
];
const getSessionTypeMeta = (name, id = 0) => ({
  label: name || "—",
  ...(SESSION_TYPE_COLORS[name] ??
    SESSION_TYPE_PALETTE[id % SESSION_TYPE_PALETTE.length]),
});

const STATUS_META = {
  PENDING_PAYMENT: {
    label: "Pending Payment",
    color: "#e65100",
    bg: "#fff3e0",
  },
  CONFIRMED: { label: "Confirmed", color: "#1565c0", bg: "#e3f2fd" },
  IN_PROGRESS: { label: "In Progress", color: "#6a1b9a", bg: "#f3e5f5" },
  COMPLETED: { label: "Completed", color: "#2e7d32", bg: "#e8f5e9" },
  CANCELLED: { label: "Cancelled", color: "#c62828", bg: "#ffebee" },
  NO_SHOW: { label: "No Show", color: "#455a64", bg: "#eceff1" },
};

const PAYMENT_META = {
  INITIATED: { label: "Initiated", color: "#1565c0", bg: "#e3f2fd" },
  CAPTURED: { label: "Captured", color: "#2e7d32", bg: "#e8f5e9" },
  FAILED: { label: "Failed", color: "#d84315", bg: "#fbe9e7" },
  REFUNDED: { label: "Refunded", color: "#455a64", bg: "#eceff1" },
  PARTIALLY_REFUNDED: {
    label: "Partially Refunded",
    color: "#6a1b9a",
    bg: "#f3e5f5",
  },
};

// A booking row can carry multiple students (group/doubt sessions) each with
// their own status/paymentStatus. Used only for sorting the Status/Payment
// columns — collapses to a single value when every student agrees, otherwise
// "MIXED" (the table itself renders each student's own value via
// GroupMetaCell, not this collapsed one).
const deriveFromStudents = (students, field) => {
  const list = students || [];
  if (!list.length) return null;
  const values = new Set(list.map((s) => s[field]));
  return values.size === 1 ? [...values][0] : "MIXED";
};

// /admin/bookings already embeds the full students[] (name/mobile/email/
// course/university/status/paymentStatus) on each row, but a group session
// still comes back as one duplicate top-level row per student sharing the
// same mentor/slot/subject/session-type — each carrying an identical
// students[] list. Keep only the first row per session so the table doesn't
// render the same group twice.
const groupBookings = (list) => {
  const seen = new Map();
  (list || []).forEach((r) => {
    const key = [
      r.mentorProfileId,
      r.slotStartTs,
      r.subjectId,
      r.sessionTypeId,
    ].join("|");
    if (seen.has(key)) return;
    seen.set(key, {
      ...r,
      students: r.students || [],
      studentName: r.students?.[0]?.studentName,
      status: deriveFromStudents(r.students, "status"),
      paymentStatus: deriveFromStudents(r.students, "paymentStatus"),
    });
  });
  return [...seen.values()];
};

const AVATAR_PALETTE = [
  "#5e35b1",
  "#1565c0",
  "#00897b",
  "#e65100",
  "#6a1b9a",
  "#2e7d32",
  "#c62828",
];
const avatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};
const initialsOf = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const MENTOR_BOOKINGS_FILTER_KEY = "MentorBookingsDateFilter";
const MENTOR_BOOKINGS_FILTERS_KEY = "MentorBookingsFilters";

const DEFAULT_FILTERS = {
  search: "",
  status: "",
  sessionType: "",
  paymentStatus: "",
};

// Stat card definitions: value comes from the /admin/bookings/summary
// response via `field`.
const SUMMARY_STAT_DEFS = [
  {
    field: "totalSessions",
    label: "Total Sessions",
    icon: <CalendarMonthIcon sx={{ fontSize: 32, color: ACCENT }} />,
    iconBg: "linear-gradient(135deg, #eef3fc, #cddcf5)",
    borderColor: ACCENT,
    glowColor: "rgba(23,71,158,0.18)",
    sub: "Matching current date range",
  },
  {
    field: "scheduled",
    label: "Scheduled",
    icon: <EventAvailableIcon sx={{ fontSize: 32, color: "#1565c0" }} />,
    iconBg: "linear-gradient(135deg, #eef4ff, #cfe1fb)",
    borderColor: "#1565c0",
    glowColor: "rgba(21,101,192,0.18)",
    sub: "Awaiting the session",
  },
  {
    field: "completed",
    label: "Completed",
    icon: <CheckCircleIcon sx={{ fontSize: 32, color: "#2e7d32" }} />,
    iconBg: "linear-gradient(135deg, #eef8ef, #c8e6c9)",
    borderColor: "#2e7d32",
    glowColor: "rgba(46,125,50,0.18)",
    sub: "Sessions held",
  },
  {
    field: "upcoming",
    label: "Upcoming",
    icon: <PendingActionsIcon sx={{ fontSize: 32, color: "#e65100" }} />,
    iconBg: "linear-gradient(135deg, #fff6ee, #ffe0b2)",
    borderColor: "#e65100",
    glowColor: "rgba(230,81,0,0.18)",
    sub: "Yet to happen",
  },
  {
    field: "cancelled",
    label: "Cancelled",
    icon: <CancelIcon sx={{ fontSize: 32, color: "#c62828" }} />,
    iconBg: "linear-gradient(135deg, #fef1f1, #ffcdd2)",
    borderColor: "#c62828",
    glowColor: "rgba(198,40,40,0.18)",
    sub: "Cancelled sessions",
  },
];

const PLACEHOLDER_SUMMARY = {
  totalSessions: 0,
  scheduled: 0,
  completed: 0,
  upcoming: 0,
  cancelled: 0,
};

function PersonCell({ name, subtext }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: avatarColor(name),
          color: "#fff",
          fontSize: "0.72rem",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {initialsOf(name)}
      </Box>
      <Box sx={{ textAlign: "left" }}>
        <Typography
          sx={{ fontSize: "0.83rem", fontWeight: 600, color: "#1a1a2e" }}
        >
          {name}
        </Typography>
        {subtext && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#43a047",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {subtext}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function MetaChip({ meta }) {
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.72rem",
        color: meta.color,
        backgroundColor: meta.bg,
      }}
    />
  );
}

// Status/Payment columns for a group session: rather than collapsing every
// student's value into one "MIXED" chip, show each student's own value
// (name + chip) stacked in the cell — a single-student booking still
// renders as one plain chip.
function GroupMetaCell({ students, field, metaMap }) {
  const list = students || [];
  if (!list.length) return "-";
  const metaFor = (value) =>
    metaMap[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  if (list.length === 1) {
    return <MetaChip meta={metaFor(list[0][field])} />;
  }
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      {list.map((s) => (
        <Box
          key={s.bookingId ?? s.studentId}
          sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
        >
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "text.secondary",
              maxWidth: 70,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {s.studentName}
          </Typography>
          <MetaChip meta={metaFor(s[field])} />
        </Box>
      ))}
    </Stack>
  );
}

// Hover/click popover shared styling for the Mentor/Student detail cells
const hoverPopoverProps = {
  disableRestoreFocus: true,
  sx: { pointerEvents: "none" },
  anchorOrigin: { vertical: "bottom", horizontal: "center" },
  transformOrigin: { vertical: "top", horizontal: "center" },
  PaperProps: {
    sx: {
      p: 1.5,
      borderRadius: 2,
      minWidth: 230,
      maxWidth: 320,
      boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
      border: "1px solid #f0f0f0",
    },
  },
};

// Mentor's mobile/email aren't shown as table columns — only on hover/click
// of the Mentor cell, to keep the table itself compact.
function MentorCell({ row }) {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <Box
        onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ display: "inline-block", cursor: "pointer" }}
      >
        <PersonCell name={row.mentorName || "—"} />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        {...hoverPopoverProps}
      >
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            color: "#999",
            textTransform: "uppercase",
            letterSpacing: 0.3,
            mb: 0.8,
            textAlign: "left",
          }}
        >
          Mentor Details
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
          <PhoneIcon sx={{ fontSize: 15, color: "#667eea" }} />
          <Typography sx={{ fontSize: "0.8rem" }}>
            {row.mentorMobileNumber || "—"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <EmailIcon sx={{ fontSize: 15, color: "#667eea" }} />
          <Typography sx={{ fontSize: "0.8rem" }}>
            {row.mentorEmail || "—"}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

// Detail content shared by each student's popover — phone, email,
// course/university and this student's own status/payment (never "MIXED":
// that only applies to the group-level column, each student here has one
// concrete status/paymentStatus).
function StudentDetailContent({ student: s }) {
  return (
    <>
      <Typography
        sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1a1a2e" }}
      >
        {s.studentName}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.3 }}>
        <PhoneIcon sx={{ fontSize: 13, color: "#667eea" }} />
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {s.mobileNumber || "—"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: 0.3 }}>
        <EmailIcon sx={{ fontSize: 13, color: "#667eea" }} />
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          {s.email || "—"}
        </Typography>
      </Box>
      <Typography
        sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.2 }}
      >
        {s.courseName || "—"} · {s.universityName || "—"}
      </Typography>
      <Stack direction="row" spacing={0.6} sx={{ mt: 0.6 }}>
        <MetaChip
          meta={
            STATUS_META[s.status] ?? {
              label: s.status ?? "—",
              color: "#555",
              bg: "#f5f5f5",
            }
          }
        />
        <MetaChip
          meta={
            PAYMENT_META[s.paymentStatus] ?? {
              label: s.paymentStatus ?? "—",
              color: "#555",
              bg: "#f5f5f5",
            }
          }
        />
      </Stack>
    </>
  );
}

// One student's name, hoverable/clickable on its own — opens a popover with
// just that student's details (not the whole group's).
function StudentNameChip({ student }) {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <Typography
        component="span"
        onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          fontSize: "0.83rem",
          fontWeight: 600,
          color: "#1a1a2e",
          cursor: "pointer",
          "&:hover": { color: "#667eea", textDecoration: "underline" },
        }}
      >
        {student.studentName}
      </Typography>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        {...hoverPopoverProps}
      >
        <StudentDetailContent student={student} />
      </Popover>
    </>
  );
}

// A booking can have 1+ students (group/doubt sessions) — every student's
// name is shown in the cell (not hidden behind a "+N" chip); hovering or
// clicking any one name reveals just that student's mobile, email,
// course/university, status and payment status.
function StudentsCell({ students }) {
  const list = students || [];
  if (!list.length) return "-";
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
      {list.map((s, i) => (
        <React.Fragment key={s.bookingId ?? s.studentId}>
          <StudentNameChip student={s} />
          {i < list.length - 1 && (
            <Typography
              component="span"
              sx={{ fontSize: "0.83rem", color: "#1a1a2e", mr: 0.4 }}
            >
              ,
            </Typography>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

// Column config — reads off the groupBookings() output (booking carries
// mentor + subject/session-type/slot; `students[]` holds 1+ students)
const allColumns = [
  {
    key: "id",
    label: "Booking ID",
    cellCss: { minWidth: "90px" },
    getValue: (r) => r.id,
  },
  {
    key: "mentorName",
    label: "Mentor",
    cellCss: { minWidth: "170px" },
    getValue: (r) => r.mentorName,
  },
  {
    key: "studentName",
    label: "Student",
    cellCss: { minWidth: "170px" },
    getValue: (r) => r.studentName,
  },
  {
    key: "sessionTypeName",
    label: "Session Type",
    cellCss: { minWidth: "130px", align: "center" },
    getValue: (r) => r.sessionTypeName,
  },
  {
    key: "subjectName",
    label: "Subject",
    cellCss: { minWidth: "220px" },
    getValue: (r) => r.subjectName,
  },
  {
    key: "slotStartTs",
    label: "Date & Time",
    cellCss: { minWidth: "160px" },
    getValue: (r) => r.slotStartTs,
  },
  {
    key: "durationMinutes",
    label: "Duration",
    cellCss: { minWidth: "100px", align: "center" },
    getValue: (r) => r.durationMinutes,
  },
  {
    key: "status",
    label: "Status",
    cellCss: { minWidth: "130px", align: "center" },
    getValue: (r) => deriveFromStudents(r.students, "status"),
  },
  {
    key: "paymentStatus",
    label: "Payment",
    cellCss: { minWidth: "120px", align: "center" },
    getValue: (r) => deriveFromStudents(r.students, "paymentStatus"),
  },
];
const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
const STORAGE_KEY = "MentorBookingsVisibleColumns";

function MentorBookings() {
  const savedDateFilter = (() => {
    try {
      return JSON.parse(localStorage.getItem(MENTOR_BOOKINGS_FILTER_KEY)) || {};
    } catch {
      return {};
    }
  })();

  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem(MENTOR_BOOKINGS_FILTERS_KEY);
      if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
    } catch {}
    return DEFAULT_FILTERS;
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("slotStartTs");
  const [order, setOrder] = useState("asc");

  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionTypesList, setSessionTypesList] = useState([]);
  const [summary, setSummary] = useState(PLACEHOLDER_SUMMARY);
  const [refreshToken, setRefreshToken] = useState(0);
  const [rescheduleRow, setRescheduleRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);

  const handleOpenReschedule = (row) => setRescheduleRow(row);
  const handleCloseReschedule = () => setRescheduleRow(null);
  const handleRescheduleSuccess = () => setRefreshToken((t) => t + 1);

  const handleOpenCancel = (row) => setCancelRow(row);
  const handleCloseCancel = () => setCancelRow(null);
  const handleCancelSuccess = () => setRefreshToken((t) => t + 1);

  const [activeDateFilter, setActiveDateFilter] = useState(
    savedDateFilter.activeFilter || "1M",
  );
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [customStart, setCustomStart] = useState(
    savedDateFilter.customStart || "",
  );
  const [customEnd, setCustomEnd] = useState(savedDateFilter.customEnd || "");

  useEffect(() => {
    localStorage.setItem(
      MENTOR_BOOKINGS_FILTER_KEY,
      JSON.stringify({
        activeFilter: activeDateFilter,
        customStart,
        customEnd,
      }),
    );
  }, [activeDateFilter, customStart, customEnd]);

  const getDateRange = (filter, start, end) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (filter === "custom") return { start, end };
    if (filter === "TODAY") return { start: fmt(today), end: fmt(today) };
    if (filter === "YESTERDAY") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: fmt(yesterday), end: fmt(yesterday) };
    }
    const rangeEnd = fmt(today);
    const rangeStart = new Date(today);
    if (filter === "1W") rangeStart.setDate(rangeStart.getDate() - 7);
    else if (filter === "1M") rangeStart.setMonth(rangeStart.getMonth() - 1);
    else if (filter === "3M") rangeStart.setMonth(rangeStart.getMonth() - 3);
    else if (filter === "6M") rangeStart.setMonth(rangeStart.getMonth() - 6);
    else rangeStart.setFullYear(rangeStart.getFullYear() - 1);
    return { start: fmt(rangeStart), end: rangeEnd };
  };

  const { start: resolvedStart, end: resolvedEnd } = getDateRange(
    activeDateFilter,
    customStart,
    customEnd,
  );

  const handleDateFilterChange = (val) => {
    setActiveDateFilter(val);
    setDateAnchorEl(null);
    setPage(0);
  };

  const handleCustomDateApply = () => {
    if (customStart && customEnd) {
      setActiveDateFilter("custom");
      setDateAnchorEl(null);
      setPage(0);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(
        MENTOR_BOOKINGS_FILTERS_KEY,
        JSON.stringify(filters),
      );
    } catch {}
  }, [filters]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  // Session types for the filter dropdown (id-based)
  useEffect(() => {
    api
      .get("/mentor-catalog/sessionTypes")
      .then((res) => setSessionTypesList(res.data?.data ?? []))
      .catch((err) => console.error("Session types fetch error:", err));
  }, []);

  // Bookings — paginated + filtered server-side
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    const params = { page, size: rowsPerPage };
    if (resolvedStart) params.startDate = resolvedStart;
    if (resolvedEnd) params.endDate = resolvedEnd;
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.sessionType) params.sessionTypeId = filters.sessionType;
    if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;

    api
      .get("/admin/bookings", { params })
      .then((res) => {
        setRows(groupBookings(res.data?.data?.content ?? []));
        setTotalElements(res.data?.data?.totalElements ?? 0);
      })
      .catch((err) => {
        console.error("Bookings fetch error:", err);
        setRows([]);
        setTotalElements(0);
        setError("Failed to load bookings. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [
    page,
    rowsPerPage,
    resolvedStart,
    resolvedEnd,
    filters.search,
    filters.status,
    filters.sessionType,
    filters.paymentStatus,
    refreshToken,
  ]);

  // Stat cards — /admin/bookings/summary, scoped to the same date range as
  // the table (not the table's other filters, matching MentorPayments.jsx).
  useEffect(() => {
    const params = {};
    if (resolvedStart) params.startDate = resolvedStart;
    if (resolvedEnd) params.endDate = resolvedEnd;

    api
      .get("/admin/bookings/summary", { params })
      .then((res) => {
        setSummary({ ...PLACEHOLDER_SUMMARY, ...(res.data?.data ?? {}) });
      })
      .catch((err) => {
        console.error("Bookings summary fetch error:", err);
        setSummary(PLACEHOLDER_SUMMARY);
      });
  }, [resolvedStart, resolvedEnd]);

  const sortedData = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (!orderBy) return 0;
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [rows, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  // Column visibility (persisted, same pattern as MentorAdminTable)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) return new Set(filtered);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderCellValue = (row, col) => {
    const value = col.getValue(row);
    if (col.key === "sessionTypeName")
      return <MetaChip meta={getSessionTypeMeta(value, row.sessionTypeId)} />;
    if (col.key === "status")
      return (
        <GroupMetaCell
          students={row.students}
          field="status"
          metaMap={STATUS_META}
        />
      );
    if (col.key === "paymentStatus")
      return (
        <GroupMetaCell
          students={row.students}
          field="paymentStatus"
          metaMap={PAYMENT_META}
        />
      );
    if (col.key === "mentorName") return <MentorCell row={row} />;
    if (col.key === "studentName")
      return <StudentsCell students={row.students} />;
    if (col.key === "slotStartTs") {
      const d = value ? new Date(value) : null;
      if (!d || Number.isNaN(d.getTime())) return "-";
      return (
        <>
          {d.toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
          <br />
          <Typography variant="caption" color="text.secondary">
            {d.toLocaleTimeString("en-IN", {
              hour: "numeric",
              minute: "2-digit",
            })}
          </Typography>
        </>
      );
    }
    if (col.key === "durationMinutes")
      return value != null ? `${value} min` : "-";
    return value ?? "-";
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      {/* Date range filter — top, standalone (mirrors MentorPayments.jsx) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2.5,
          mx: "15px",
        }}
      >
        <Box
          onClick={(e) => setDateAnchorEl(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: "pointer",
            background: "rgba(255,255,255,0.85)",
            border: "1px solid #e8eaf0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 16, color: "#667eea" }} />
          <Typography
            sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#444" }}
          >
            {activeDateFilter === "custom" && customStart && customEnd
              ? `${customStart} – ${customEnd}`
              : activeDateFilter === "TODAY"
                ? "Today"
                : activeDateFilter === "YESTERDAY"
                  ? "Yesterday"
                  : activeDateFilter === "1W"
                    ? "Last Week"
                    : activeDateFilter === "1M"
                      ? "Last 1 Month"
                      : activeDateFilter === "3M"
                        ? "Last 3 Months"
                        : activeDateFilter === "6M"
                          ? "Last 6 Months"
                          : "Last 1 Year"}
          </Typography>
          <Box sx={{ fontSize: 10, color: "#aaa", ml: 0.5 }}>▼</Box>
        </Box>

        <Popover
          open={Boolean(dateAnchorEl)}
          anchorEl={dateAnchorEl}
          onClose={() => setDateAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
              minWidth: 220,
              overflow: "hidden",
              border: "1px solid #f0f0f0",
            },
          }}
        >
          {[
            { label: "Today", value: "TODAY" },
            { label: "Yesterday", value: "YESTERDAY" },
            { label: "Last Week", value: "1W" },
            { label: "Last 1 Month", value: "1M" },
            { label: "Last 3 Months", value: "3M" },
            { label: "Last 6 Months", value: "6M" },
            { label: "Last 1 Year", value: "1Y" },
          ].map((opt) => (
            <Box
              key={opt.value}
              onClick={() => handleDateFilterChange(opt.value)}
              sx={{
                px: 2.5,
                py: 1.2,
                cursor: "pointer",
                fontSize: "0.83rem",
                fontWeight: activeDateFilter === opt.value ? 700 : 500,
                color: activeDateFilter === opt.value ? "#667eea" : "#444",
                background:
                  activeDateFilter === opt.value
                    ? "rgba(102,126,234,0.07)"
                    : "#fff",
                borderLeft:
                  activeDateFilter === opt.value
                    ? "3px solid #667eea"
                    : "3px solid transparent",
                transition: "all 0.15s",
                "&:hover": {
                  background: "rgba(102,126,234,0.06)",
                  color: "#667eea",
                },
              }}
            >
              {opt.label}
            </Box>
          ))}

          <Box sx={{ height: "1px", background: "#f0f0f0", mx: 2 }} />

          <Box sx={{ p: 2 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="#aaa"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 1,
                fontSize: "0.68rem",
              }}
            >
              Custom Range
            </Typography>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 1 }}
            >
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customStart }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setCustomStart("");
                    setCustomEnd("");
                    setActiveDateFilter("1M");
                    setDateAnchorEl(null);
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    borderColor: "#e0e0e0",
                    color: "#888",
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  onClick={handleCustomDateApply}
                  disabled={!customStart || !customEnd}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    boxShadow: "none",
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Popover>
      </Box>

      {/* Stat cards — backed by /admin/bookings/summary, scoped to the
          selected date range. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(5, 1fr)",
          },
          gap: 2.5,
          mb: 3,
          mx: "15px",
        }}
      >
        {SUMMARY_STAT_DEFS.map((def) => ({
          ...def,
          value: summary[def.field] ?? 0,
        })).map((stat) => (
          <Card
            key={stat.label}
            sx={{
              ...cardStyle,
              boxShadow: `0 4px 24px ${stat.glowColor}, 0 1px 4px rgba(0,0,0,0.06)`,
              borderTop: `4px solid ${stat.borderColor}`,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: `0 8px 32px ${stat.glowColor}, 0 2px 8px rgba(0,0,0,0.08)`,
              },
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Box
                  sx={{
                    background: stat.iconBg,
                    borderRadius: 2,
                    width: 56,
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: `0 2px 8px ${stat.glowColor}`,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: "0.78rem" }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    fontWeight={800}
                    sx={{ fontSize: "1.7rem", color: "#1a1a2e" }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {stat.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Table filter bar — mirrors MentorPayoutsTable/MentorAdminTable's toolbar Card */}
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flex: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search mentor or student"
                value={filters.search}
                onChange={(e) => setFilter("search", e.target.value)}
                sx={{ minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl
                size="small"
                sx={{ minWidth: 150, textAlign: "left" }}
              >
                <InputLabel shrink>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => setFilter("status", e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Status</MenuItem>
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <MenuItem key={key} value={key}>
                      {meta.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ minWidth: 140, textAlign: "left" }}
              >
                <InputLabel shrink>Session Type</InputLabel>
                <Select
                  label="Session Type"
                  value={filters.sessionType}
                  onChange={(e) => setFilter("sessionType", e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Types</MenuItem>
                  {sessionTypesList.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ minWidth: 160, textAlign: "left" }}
              >
                <InputLabel shrink>Payment Status</InputLabel>
                <Select
                  label="Payment Status"
                  value={filters.paymentStatus}
                  onChange={(e) => setFilter("paymentStatus", e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Payment Status</MenuItem>
                  {Object.entries(PAYMENT_META).map(([key, meta]) => (
                    <MenuItem key={key} value={key}>
                      {meta.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}
                variant="body2"
                color="text.secondary"
              >
                {sortedData.length} of {totalElements} sessions
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Reset filters" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleReset}
                    disabled={activeFilterCount === 0}
                    sx={{
                      ...iconBtnSx,
                      color: activeFilterCount === 0 ? "#bdbdbd" : "#ef5350",
                      borderColor:
                        activeFilterCount === 0
                          ? "rgba(0,0,0,0.08)"
                          : "rgba(239,83,80,0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(239,83,80,0.08)",
                        borderColor: "#ef5350",
                        color: "#ef5350",
                      },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ViewColumnIcon />}
                  onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Columns
                </Button>
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={openColumnsMenu}
                  onClose={() => setColumnsMenuAnchor(null)}
                  PaperProps={{
                    sx: {
                      width: 200,
                      maxHeight: 500,
                      overflowY: "auto",
                      borderRadius: 2,
                    },
                  }}
                >
                  {allColumns.map((col) => (
                    <FormControlLabel
                      key={col.key}
                      control={
                        <Checkbox
                          checked={visibleColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                        />
                      }
                      label={col.label}
                      sx={{ pl: 1, pr: 2 }}
                    />
                  ))}
                </Menu>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={dense}
                    onChange={(e) => setDense(e.target.checked)}
                    color="primary"
                  />
                }
                label="Dense"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert
          severity="error"
          sx={{ mx: "15px", mt: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && (
        <Fade in={!isLoading}>
          {/* Sessions table */}
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            <TableContainer
              sx={{ maxHeight: "calc(100vh - 220px)", overflowX: "auto" }}
            >
              <Table stickyHeader size={dense ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px",
                          backgroundColor: ACCENT,
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          ...c.cellCss,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={whiteSortLabelSx}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: ACCENT,
                        color: "white",
                        fontWeight: 600,
                        minWidth: 130,
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Typography variant="h6" color="text.secondary">
                          No sessions match the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        }}
                      >
                        {visibleColumnDefs.map((c) => (
                          <TableCell
                            key={c.key}
                            sx={{ padding: "10px 10px", ...c.cellCss }}
                          >
                            {renderCellValue(row, c)}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            padding: "10px 10px",
                            minWidth: "130px",
                            position: "sticky",
                            backgroundColor: "#ffffff",
                            right: 0,
                            zIndex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            {/* <Tooltip title="View Session" arrow placement="top">
                              <IconButton
                                color="primary"
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}
                            <Tooltip title="Reschedule" arrow placement="top">
                              <IconButton
                                color="warning"
                                size="small"
                                onClick={() => handleOpenReschedule(row)}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "warning.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EventRepeatIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Cancel Session"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleOpenCancel(row)}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "error.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}

      <ToastContainer />
      <MentorRescheduleDialog
        open={Boolean(rescheduleRow)}
        booking={rescheduleRow}
        onClose={handleCloseReschedule}
        onSuccess={handleRescheduleSuccess}
      />
      <MentorCancelBookingDialog
        open={Boolean(cancelRow)}
        booking={cancelRow}
        onClose={handleCloseCancel}
        onSuccess={handleCancelSuccess}
      />
    </Box>
  );
}

export default MentorBookings;
